
import Vue from 'vue';

import ToolTip from '@/components/common/ToolTip.vue';
import NotificationList from '@/components/notifications/NotificationList.vue';
import NotificationEditModal from '@/components/modal/NotificationEditModal.vue';
import ConfirmActionModal from '@/components/modal/ConfirmActionModal.vue';

import { Subscription, Subscriber } from '@/interfaces/notifications';
import permissions from '@/lib/permissions';
import { AxiosError } from 'axios';

interface ComponentData {
	removalCandidate: Subscriber | null;
}

export default Vue.extend({
	components: {
		ToolTip,
		NotificationList,
		NotificationEditModal,
		ConfirmActionModal,
	},

	data(): ComponentData {
		return {
			removalCandidate: null,
		};
	},

	mounted() {
		if (this.subscription === undefined) {
			this.createInitialSubscription();
		}
	},

	computed: {
		subscription(): Subscription | undefined {
			return this.$store.getters['notifications/subscriptions/getSubscriptions'].filter(
				(s: Subscription) => s.fk_alert_types === null
			)[0];
		},

		userCanReadNotices(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'notices',
				'read',
			]);
		},

		userCanWriteNotices(): boolean {
			return permissions.hasPermission(this.$store.getters['usermanagement/user/getRole'], [
				'notices',
				'write',
			]);
		},

		subscribers(): Subscriber[] {
			return this.$store.getters['notifications/subscribers/getSubscribers'].filter(
				(s: Subscriber) => s.fk_notification_subscription === this.subscription?.id
			);
		},
	},

	methods: {
		async createInitialSubscription(): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			// Check that we have loaded the notifications first
			try {
				await this.$store.dispatch('notifications/subscriptions/fetchSubscriptions');
				if (this.subscription === undefined) {
					const site = this.$store.getters['tenancy/sites/getSelectedSite']!;
					await this.$store.dispatch('notifications/subscriptions/addSubscription', {
						subscription: {
							fk_client: this.$store.getters['tenancy/clients/getSelectedClient']!.id,
							fk_site: site.id,
							fk_alert_types: null,
							notification_text: `A new error has been detected in the lighting setup of ${site.name}`,
							notification_subject: 'The EAS Intelligent Lighting system has detected a problem',
						},
					});
				}
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-notification-fetch',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		makeRemoveArgs(): { receiver: string; address: string } {
			return {
				receiver: this.removalCandidate?.name ?? this.$t('Unknown').toString(),
				address: this.removalCandidate?.address ?? this.$t('Unknown').toString(),
			};
		},

		onAddSubscriber(): void {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(this.$refs.subscriberModal as any).show();
		},

		onChangeSubscriber(subscriber: Subscriber): void {
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(this.$refs.subscriberModal as any).show(subscriber);
		},

		async onSubscriberCreate(subscriber: Partial<Subscriber>): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				await this.$store.dispatch('notifications/subscribers/addSubscriber', {
					subscriber: {
						fk_client: this.$store.getters['tenancy/clients/getSelectedClient']!.id,
						fk_site: this.$store.getters['tenancy/sites/getSelectedSite']!.id,
						fk_notification_subscription: this.subscription!.id,
						...subscriber,
					},
				});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(this.$refs.subscriberModal as any).hide();
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-create-subscription',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		async onSubscriberModify(subscriber: Subscriber): Promise<void> {
			this.$store.dispatch('tools/loading/changeLoadingStatus');
			try {
				await this.$store.dispatch('notifications/subscribers/modifySubscriber', {
					id: subscriber.id,
					subscriber,
				});
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(this.$refs.subscriberModal as any).hide();
			} catch (error) {
				this.$store.dispatch('tools/messageHandling/addErrorMessage', {
					id: 'error-modify-subscription',
					message: (error as AxiosError<{ message: string }>).response!.data.message,
				});
			}
			this.$store.dispatch('tools/loading/changeLoadingStatus');
		},

		onRemoveSubscriber(subscriber: Subscriber): void {
			this.removalCandidate = subscriber;
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(this.$refs.confirmationModal as any).show();
		},

		async onDeleteConfirm(): Promise<void> {
			if (this.removalCandidate !== null) {
				this.$store.dispatch('tools/loading/changeLoadingStatus');
				try {
					await this.$store.dispatch('notifications/subscribers/removeSubscriber', {
						id: this.removalCandidate.id,
					});
				} catch (error) {
					this.$store.dispatch('tools/messageHandling/addErrorMessage', {
						id: 'error-remove-subscription',
						message: (error as AxiosError<{ message: string }>).response!.data.message,
					});
				}
				this.onDeleteCancel();
				this.$store.dispatch('tools/loading/changeLoadingStatus');
			}
		},

		onDeleteCancel() {
			this.removalCandidate = null;

			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			(this.$refs.confirmationModal as any).hide();
		},
	},
});
